import type { TypedDocumentNode } from '@apollo/client'
import { gql } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, stripSearchParams, useRouterState } from '@tanstack/react-router'
import { SFProfileBanner } from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import { profileNameFromLayoutMatches } from '@web/src/features/profile/profile-layout-head'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { FriendSortEnum } from '@web/src/lib/graphql/types.generated'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { EllipsisIcon, UserXIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import type {
  DeleteFriendByIdMutation,
  DeleteFriendByIdMutationVariables,
  FriendsPageQuery,
  FriendsPageQueryVariables,
} from 'types/graphql'
import { z } from 'zod'

const QUERY: TypedDocumentNode<FriendsPageQuery, FriendsPageQueryVariables> = gql`
  query FriendsPage($page: Int, $filter: FriendPageFilter, $sortBy: FriendSortEnum) {
    friends: friendsPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        id
        friend {
          ...ProfileInfo
        }
      }
      count
      perPage
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

export const DELETE_FRIEND_BY_ID: TypedDocumentNode<DeleteFriendByIdMutation, DeleteFriendByIdMutationVariables> = gql`
    mutation DeleteFriendById($id: String!) {
      deleteFriendById(id: $id) 
    }
  `

type FriendsListSort = 'DISTANCE' | 'OLDEST' | 'LATEST' | 'NAME'

type FriendsSearchInput = {
  name?: string
  page?: number
  sortBy?: FriendsListSort
}

function resolveFriendsSearchParams(search: FriendsSearchInput): {
  page: number
  name?: string
  sortBy: FriendsListSort
} {
  return {
    page: search.page ?? 1,
    name: search.name,
    sortBy: search.sortBy ?? 'NAME',
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/profile/$profileId/friends/')({
  component: PageComponent,
  validateSearch: z.object({
    name: z.string().optional(),
    page: z.coerce.number().optional(),
    sortBy: z.enum(['DISTANCE', 'OLDEST', 'LATEST', 'NAME']).optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        sortBy: 'NAME',
      }),
    ],
  },
  loaderDeps: ({ search }) => resolveFriendsSearchParams(search),
  head: ({ matches }) => {
    const profileName = profileNameFromLayoutMatches(matches)
    const title = profileName ? `${profileName}'s Friends | ${env.VITE_APP_NAME}` : `Friends | ${env.VITE_APP_NAME}`
    return seo({
      title,
      description: 'View and manage your friends list. Browse friends, search by name, and manage friend requests.',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    })
  },
  loader: async ({ context, params, deps }) => {
    const { queryRef } = await queryAndPreload(context, QUERY, {
      variables: {
        page: deps.page,
        sortBy: deps.sortBy as FriendSortEnum,
        filter: {
          profileId: params.profileId,
          name: deps.name,
        },
      },
    })
    return { queryRef }
  },
})

function PageComponent() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const { data } = useReadQuery(queryRef as QueryRef<FriendsPageQuery>)
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const search = Route.useSearch()
  const resolvedSearch = useMemo(() => resolveFriendsSearchParams(search), [search])
  const { profileId } = Route.useParams()
  const [modalDialog, setModalDialog] = useState(false)
  const [deleteFriendId, setDeleteFriendId] = useState<string | null>(null)
  const navigate = Route.useNavigate()
  const { currentUser } = useAuth()
  const [showLoading, setShowLoading] = useState(false)
  const isLoading = useRouterState({ select: (s) => s.status === 'pending' })

  const formMethods = useForm({
    defaultValues: {
      name: search.name,
      sortBy: resolvedSearch.sortBy,
    },
  })

  useEffect(() => {
    if (!isLoading) {
      setShowLoading(false)
    }
  }, [isLoading])

  const [deleteFriend] = useMutation(DELETE_FRIEND_BY_ID, {
    onCompleted: () => {
      toast.success(t('Profile Unfriended'))
      setShowSpinnerValue(false)
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
    update(cache, _, { variables }) {
      cache.modify({
        id: 'ROOT_QUERY',
        fields: {
          friendsCounts(existing: { friends?: number } | undefined, { storeFieldName }) {
            if (!storeFieldName.includes(profileId) || existing == null) return existing
            return {
              ...existing,
              friends: Math.max(0, (existing.friends ?? 0) - 1),
            }
          },
        },
      })
      cache.evict({ id: cache.identify({ __typename: 'Friend', id: variables?.id }) })
      cache.gc()
    },
  })

  const handleDialogAccept = () => {
    if (!deleteFriendId) return
    setShowSpinnerValue(true)
    setModalDialog(false)
    deleteFriend({ variables: { id: deleteFriendId } })
  }

  const handleFilter = ({ page, name, sortBy }: { page?: number; name?: string; sortBy?: string }): void => {
    setShowLoading(true)
    navigate({
      search: {
        page: page ?? resolvedSearch.page,
        sortBy: sortBy ?? resolvedSearch.sortBy,
        name: name ?? search.name,
      },
      replace: true,
    } as never)
  }

  const entryCount = data?.friends?.entries?.length ?? 0

  return (
    <div className="mx-auto w-full max-w-[90rem] px-0">
      <div className="flex w-full flex-col gap-4">
        <h1 className="sr-only">{t('Friends Listing')}</h1>

        <Form className="w-full" formMethods={formMethods} suppressHydrationWarning>
          <SearchExpand
            className="w-full"
            clearSearchLabel={t('Clear search')}
            fieldProps={{ className: 'basis-0 flex-1 min-w-0' }}
            loading={showLoading}
            name="name"
            onSearchChange={(value) => handleFilter({ name: value })}
            placeholder={t('Search friends...')}
            rightBarChildren={
              <div className="flex w-[min(100%,180px)] shrink-0 basis-[180px] items-start">
                <NativeSelectFormField
                  aria-label={t('Sort By')}
                  fieldProps={{ className: 'w-full max-w-full' }}
                  name="sortBy"
                  onValueChange={(e) => handleFilter({ sortBy: e.target.value })}
                  selectProps={{ className: 'w-full min-w-0' }}
                >
                  <option value="NAME">{t('Name')}</option>
                  <option value="LATEST">{t('Latest')}</option>
                  <option value="OLDEST">{t('Oldest')}</option>
                  <option value="DISTANCE">{t('Distance')}</option>
                </NativeSelectFormField>
              </div>
            }
            toggleFiltersLabel={t('Toggle filters')}
          />
        </Form>

        {entryCount > 0 ? (
          <div className={cn('grid w-full gap-2', entryCount > 1 ? 'lg:grid-cols-2' : 'grid-cols-1')}>
            {data?.friends?.entries.map((friend) => {
              return (
                <Card className="p-2" key={friend.id}>
                  <CardContent className="p-0">
                    <SFProfileBanner
                      lowerChild={`${friend?.friend?.location}`}
                      profile={friend.friend}
                      rightChild={
                        currentUser?.profile?.id === profileId && (
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              render={
                                <Button
                                  aria-label={t('Menu')}
                                  className="rounded-full"
                                  disabled={showSpinner}
                                  size="icon-sm"
                                  type="button"
                                  variant="default"
                                >
                                  {showSpinner ? <Spinner className="size-4" /> : <EllipsisIcon className="size-4" />}
                                </Button>
                              }
                            />
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  setDeleteFriendId(friend.id)
                                  setModalDialog(true)
                                }}
                              >
                                <UserXIcon className="mr-2 size-4" />
                                {t('Unfriend')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )
                      }
                      shape="rounded"
                      size="2xl"
                    />
                  </CardContent>
                </Card>
              )
            })}
          </div>
        ) : (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <UserXIcon />
              </EmptyMedia>
              <EmptyTitle>
                {search.name
                  ? t('No friends found for "{{name}}"', { name: search.name })
                  : t('No friends at this time')}
              </EmptyTitle>
            </EmptyHeader>
          </Empty>
        )}

        <PaginationControl
          count={data?.friends?.count || 0}
          onPageChange={(page) => handleFilter({ page: page.page })}
          page={resolvedSearch.page}
          pageSize={data?.friends?.perPage}
        />
      </div>

      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Yes')}
        loading={showSpinner}
        onConfirm={handleDialogAccept}
        onOpenChange={(e) => setModalDialog(e.open)}
        open={modalDialog}
        text={t('Are you sure you want to unfriend this profile?')}
        title={t('Are you sure?')}
        variant="basic"
      />
    </div>
  )
}
